
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { TwoFactorVerificationDialog } from "@/components/auth/TwoFactorVerificationDialog";
import { getRememberedEmail, saveRememberedEmail, clearRememberedEmail, getRememberMePreference, clearAllAuthStorage } from "@/utils/authStorage";
import { isRestrictedBrowserContext } from "@/utils/browserDetection";
import { supabase } from "@/integrations/supabase/client";

const LoginPage = () => {
  const [email, setEmail] = useState(getRememberedEmail('seller'));
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(getRememberMePreference('seller'));
  const [error, setError] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const { signIn, isLoading, pending2FA, pending2FAUserId, pending2FAMethod, complete2FAVerification, signOut } = useAuth();
  
  // Clean up stale auth data in restricted browser contexts (WebViews, Chrome Custom Tabs)
  // This prevents the "Signing In..." hang that occurs due to isolated storage
  useEffect(() => {
    const cleanupStaleAuth = async () => {
      if (isRestrictedBrowserContext()) {
        console.log('Restricted browser context detected (WebView/Chrome Custom Tab), clearing stale auth data');
        clearAllAuthStorage();
        await supabase.auth.signOut().catch(() => {
          // Ignore errors - we just want to ensure clean state
        });
      }
    };
    cleanupStaleAuth();
  }, []);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);
    setRateLimitError(null);
    
    // Validate inputs
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      await signIn(email, password, rememberMe);
      // Save or clear remembered email based on checkbox
      if (rememberMe) {
        saveRememberedEmail(email, 'seller');
      } else {
        clearRememberedEmail('seller');
      }
    } catch (error: any) {
      if (error.message && error.message.includes('Too many attempts')) {
        setRateLimitError(error.message);
      } else {
        setError(error.message || 'Sign in failed. Please try again.');
      }
    }
  };
  
  return (
    <>
      <Card className="w-full border-border bg-card animate-fade-in-stable">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Welcome Back</CardTitle>
          <CardDescription className="text-center">
            Enter your email to sign in to your account
          </CardDescription>
        </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {rateLimitError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{rateLimitError}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-muted"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                to="/seller/forgot-password"
                className="text-sm text-godyspo-blue hover:text-godyspo-blue/90"
              >
                Forgot password?
              </Link>
            </div>
            <PasswordInput
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-muted"
              autoComplete="current-password"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(!!checked)}
            />
            <Label htmlFor="remember" className="text-sm font-normal">
              Remember me
            </Label>
          </div>
          
          <Button
            type="submit"
            className="w-full bg-godyspo-gold hover:bg-godyspo-gold/90 text-black touch-manipulation"
            disabled={isLoading}
            style={{ minHeight: '44px' }} // Ensure minimum touch target size on mobile
          >
            {isLoading ? "Signing In..." : "Sign In"}
          </Button>
          
          <div className="text-center">
            <span className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/seller/register" className="text-godyspo-blue hover:text-godyspo-blue/90 underline-offset-4 hover:underline">
                Sign up
              </Link>
            </span>
          </div>
          
          <div className="text-center mt-2">
            <span className="text-sm text-muted-foreground">
              Are you a buyer?{" "}
              <Link to="/buyer/login" className="text-godyspo-blue hover:text-godyspo-blue/90 underline-offset-4 hover:underline">
                Login here
              </Link>
            </span>
          </div>
        </form>
      </CardContent>
    </Card>

      <TwoFactorVerificationDialog
        open={pending2FA}
        userId={pending2FAUserId || ''}
        onVerified={complete2FAVerification}
        onCancel={async () => {
          await signOut();
        }}
      />
    </>
  );
};

export default LoginPage;
